<script setup>
import { useField } from 'vee-validate'

const props = defineProps({
    checkboxConfig: {
        type: Object,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                name: value => typeof value.name === 'string',
                value: value => ['string', 'number', 'boolean'].includes(typeof value.value),
                id: value => typeof value.id === 'string',
                label: value => typeof value.label === 'string',
                validationRules: value => value.validationRules === undefined || typeof value.validationRules === 'object',
                smart: value => value.smart === undefined || typeof value.smart === 'boolean',
                val: value => value.val === undefined || Array.isArray(value.val),
                required: value => value.required === undefined || typeof value.required === 'boolean',
                pending: value => value.pending === undefined || typeof value.pending === 'boolean',
                multiLine: value => value.multiLine === undefined || typeof value.multiLine === 'boolean',
            }

            return Object.values(validations).every(validation => validation(rawValue))
        }
    }
})

//  Creates a form field with its validation state, you will use this inside your custom input components.
const { value, errorMessage, meta, resetField } = useField(() =>
    props.checkboxConfig?.name, props.checkboxConfig?.validationRules
)

// Show error + success classes in case fields have validation rules
const requiresValidation = computed(() => props.checkboxConfig?.validationRules !== undefined)

const formFieldClass = computed(() => {
    return {
        'form-error': !meta.valid && meta.dirty && requiresValidation.value,
        'form-success': meta.valid && meta.dirty && requiresValidation.value,
        'form-input-smart-checkbox-wrapper': props.checkboxConfig?.smart,
        'form-input-checkbox-wrapper': !props.checkboxConfig?.smart,
        'form-input-checkbox-multiline': props.checkboxConfig?.multiLine
    }
})

// Change initial value with dynamic data of input field
resetField({
    value: props.checkboxConfig?.val ? props.checkboxConfig?.val : []
})

// Use watcher to update data with dynamic values (seems to be necessary...?)
watch(() => props.checkboxConfig?.val, () => {
    resetField({
        value: props.checkboxConfig?.val ? props.checkboxConfig?.val : []
    })
})
</script>)

<template>
    <div :class="formFieldClass">
        <input
            :id="checkboxConfig.id"
            v-model="value"
            class="form-field-checkbox"
            type="checkbox"
            :required="checkboxConfig.required"
            :name="checkboxConfig.name"
            :value="checkboxConfig.value"
        >
        <label class="form-field-checkbox-label-wrapper" :for="checkboxConfig.id">
            <span class="form-field-checkbox-label" :class="checkboxConfig.required">
                {{ checkboxConfig.label }}
            </span>
            <LoadingSpinner v-if="checkboxConfig.pending" class="form-loader smart-checkbox-form-loader" />
        </label>

        <FormFieldMessage
            :msg-config="{
                message: errorMessage,
                error: (!!errorMessage) && meta.dirty
            }"
        />
    </div>
</template>

<style lang="scss">
.form-field-checkbox {
    @include form-field-checkbox;
}

.form-field-checkbox-label {
    @include form-field-checkbox-label;
}

.form-field-checkbox-label-wrapper {
    @include form-field-checkbox-label-wrapper;
}

.form-input-smart-checkbox-wrapper {
    @include form-input-smart-checkbox-wrapper;
}

.form-input-checkbox-wrapper {
    @include checkbox-wrapper;
}
</style>
